import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Anchor, Badge, Group } from '@mantine/core';
import { IconBuilding, IconLayoutGrid } from '@tabler/icons-react';
import { ResourceListPage } from '@/shared/components/ResourceListPage';
import { relationshipLabel } from '@/features/tenants/groupTree';
import { QuickCreateButton } from '@/shared/components/QuickCreateButton';
import { MaeShell, MaePageHead } from '@/shared/desktop/MaeShell';
import { mw } from '@/shared/mobile/tokens';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { MobileTenants } from '@/features/admin/mobile/MobileTenants';
import type { Tenant } from '@/shared/types/entities';
import { DesktopTenants } from '@/features/admin/desktop/DesktopTenants';
import { EnterCompanyModal, type CompanyToEnter } from '@/features/tenants/EnterCompanyModal';
import { CompanyActionsMenu, type CompanyRow } from '@/features/tenants/CompanyActionsMenu';

export const Route = createFileRoute('/_app/admin/tenants/')({
  validateSearch: (s: Record<string, unknown>): { view?: 'table' } => ({ view: s.view === 'table' ? 'table' : undefined }),
  component: TenantsPage,
});

/** Mobile = lista MaeDesign; desktop: default card-grid, `?view=table` = ResourceListPage. */
function TenantsPage() {
  const isMobile = useIsMobileShell();
  const { view } = Route.useSearch();
  if (isMobile) return <MobileTenants />;
  return view === 'table' ? <TenantsListPage /> : <DesktopTenants />;
}

function TenantsListPage() {
  const navigate = useNavigate();
  /* Un clic su un'azienda propone di entrarci: la scheda da fuori (`/admin/tenants/<id>`) non c'è più. */
  const [toEnter, setToEnter] = useState<CompanyToEnter | null>(null);
  return (
    <MaeShell>
      <Anchor size="sm" style={{ display: 'inline-block', marginBottom: 10, color: mw.accentText, fontWeight: 600, cursor: 'pointer' }} onClick={() => void navigate({ to: '/admin/tenants' })}>
        <Group gap={4}><IconLayoutGrid size={14} /> Vista a schede</Group>
      </Anchor>
      <MaePageHead
        icon={<IconBuilding size={22} />}
        title="Aziende"
        sub="Solo SuperAdmin gestisce le aziende del sistema. Il branding dell’azienda corrente sta in Organizzazione DP › Dati dell’azienda."
        tone="bordeaux"
      />
      <ResourceListPage<Tenant>
        title=""
        type="tenants"
        initialPageSize={50}
        columns={[
          { accessor: 'name', title: 'Nome' },
          { accessor: 'organization', title: 'Organizzazione' },
          { accessor: 'plan', title: 'Piano', width: 110, render: (row) => <Badge variant="light">{row.plan ?? 'free'}</Badge> },
          { accessor: 'enabled', title: 'Attivo', width: 90, render: (row) => row.enabled ? <Badge color="green">Sì</Badge> : <Badge color="red">No</Badge> },
          { accessor: 'email', title: 'Email' },
          { accessor: 'vatNumber', title: 'P.IVA', width: 140 },
          { accessor: 'country', title: 'Paese', width: 80 },
          /*
           * Il legame societario come **colonna**, e non come interruttore in cima.
           *
           * ⚠️ I filtri di questo elenco sono quelli di colonna: «Attivo» e «Paese» filtrano già
           * perché le colonne ci sono. Mancava il legame, quindi «solo holding» non si poteva
           * fare qui — ed è la ragione per cui viveva sull'organigramma, dove invece non serve:
           * là le righe sono il proprio ramo, poche e conosciute.
           *
           * Aggiunta la colonna, il filtro esiste con l'idioma della pagina invece che come un
           * terzo controllo che fa la stessa cosa in un altro modo.
           */
          {
            accessor: 'relationshipType',
            title: 'Legame',
            width: 130,
            render: (row) => relationshipLabel(row.relationshipType),
          },
          {
            accessor: 'id',
            title: '',
            width: 56,
            render: (row) => (
              <CompanyActionsMenu
                company={row as CompanyRow}
                onEnter={() => setToEnter({ id: row.id, name: row.name ?? 'Azienda' })}
              />
            ),
          },
        ]}
        searchField="name"
        onRowClick={(t) => setToEnter({ id: t.id, name: t.name ?? 'Azienda' })}
        emptyMessage="Nessuna azienda"
        emptyDescription="Crea la prima azienda con il bottone «Nuova azienda» nella toolbar."
        emptyIcon={IconBuilding}
        toolbarRight={
          <QuickCreateButton
            type="tenants"
            title="Nuova azienda"
            fields={[
              { name: 'name', label: 'Nome', required: true },
              { name: 'organization', label: 'Organizzazione' },
              { name: 'email', label: 'Email', placeholder: 'info@azienda.it' },
              { name: 'vatNumber', label: 'P.IVA' },
              { name: 'taxId', label: 'Codice fiscale' },
              { name: 'country', label: 'Paese (ISO2)', defaultValue: 'IT' },
              { name: 'defaultCurrency', label: 'Valuta', kind: 'select', options: [
                { value: 'EUR', label: 'EUR' },
                { value: 'USD', label: 'USD' },
                { value: 'GBP', label: 'GBP' },
                { value: 'CHF', label: 'CHF' },
              ], defaultValue: 'EUR' },
              { name: 'plan', label: 'Piano', kind: 'select', options: [
                { value: 'free', label: 'Free' },
                { value: 'starter', label: 'Starter' },
                { value: 'basic', label: 'Basic' },
                { value: 'pro', label: 'Pro' },
                { value: 'enterprise', label: 'Enterprise' },
              ], defaultValue: 'free' },
              { name: 'enabled', label: 'Attivo', kind: 'boolean', defaultValue: true },
            ]}
          />
        }
      />
      <EnterCompanyModal company={toEnter} onClose={() => setToEnter(null)} />
    </MaeShell>
  );
}
